import { useState, useCallback } from 'react';
import { AlertTriangle, Info, AlertCircle, Loader2, X } from 'lucide-react';

interface ConfirmDialogProps {
  isOpen: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
}

const variantConfig = {
  danger: {
    icon: AlertTriangle,
    iconBg: 'bg-danger/10',
    iconColor: 'text-danger',
    buttonBg: 'bg-danger hover:bg-danger/90',
  },
  warning: {
    icon: AlertCircle,
    iconBg: 'bg-warning/10',
    iconColor: 'text-warning',
    buttonBg: 'bg-warning hover:bg-warning/90',
  },
  info: {
    icon: Info,
    iconBg: 'bg-info/10',
    iconColor: 'text-info',
    buttonBg: 'bg-accent-1 hover:bg-accent-2',
  },
};

export default function ConfirmDialog({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'danger',
  isLoading = false,
}: ConfirmDialogProps) {
  if (!isOpen) return null;

  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={isLoading ? undefined : onCancel}
      />
      <div className="relative bg-bg-1 rounded-xl shadow-xl border border-border-1 max-w-md w-full mx-4 p-6">
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="absolute top-4 right-4 text-text-3 hover:text-text-1 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 p-2 rounded-lg ${config.iconBg}`}>
            <Icon className={`w-6 h-6 ${config.iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-text-1">{title}</h3>
            <p className="mt-2 text-sm text-text-2">{message}</p>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-text-2 bg-bg-3 rounded-lg hover:bg-bg-hover transition-colors disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 ${config.buttonBg}`}
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// Hook para facilitar o uso do ConfirmDialog
interface ConfirmState {
  title: string;
  message: string;
  onConfirm: () => void | Promise<void>;
  variant?: 'danger' | 'warning' | 'info';
  confirmText?: string;
}

export function useConfirmDialog() {
  const [state, setState] = useState<ConfirmState | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const confirm = useCallback((config: ConfirmState) => {
    setState(config);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!state) return;
    setIsLoading(true);
    try {
      await state.onConfirm();
    } finally {
      setIsLoading(false);
      setState(null);
    }
  }, [state]);

  const handleCancel = useCallback(() => {
    setState(null);
  }, []);

  const dialogProps = state
    ? {
        isOpen: true,
        title: state.title,
        message: state.message,
        variant: state.variant,
        confirmText: state.confirmText,
        onConfirm: handleConfirm,
        onCancel: handleCancel,
        isLoading,
      }
    : { isOpen: false, title: '', message: '', onConfirm: () => {}, onCancel: () => {} };

  return { confirm, dialogProps };
}
